import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Pagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom"; 
import api from "../../services/api/api";

const normalizarTexto = (texto) => {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const calcularDias = (dataPostagem) => {
  const dataAtual = new Date();
  const data = new Date(dataPostagem);
  const diferencaMs = dataAtual - data;
  return Math.floor(diferencaMs / (1000 * 60 * 60 * 24));
};

const Noticias = () => {
  const theme = useTheme();
  const navigate = useNavigate(); 
  const [noticias, setNoticias] = useState([]);
  const [filteredNoticias, setFilteredNoticias] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [periodo, setPeriodo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [categoria, setCategoria] = useState("");
  const [page, setPage] = useState(1);
  const noticiasPorPagina = 20;

  useEffect(() => {
    const fetchNoticias = async () => {
      try {
        const response = await api.get("/api/getAllNews");
        const filteredNews = response.data
          .filter((noticia) => noticia.isPublished)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const uniqueCategorias = [
          ...new Set(filteredNews.map((noticia) => noticia.category)),
        ];

        setNoticias(filteredNews);
        setFilteredNoticias(filteredNews);
        setCategorias(uniqueCategorias);
      } catch (error) {
        console.error("Erro ao buscar notícias:", error);
      }
    };
    fetchNoticias();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [periodo, descricao, categoria]);

  const handleSearch = () => {
    const filtros = noticias.filter((noticia) => {
      const dataNoticia = new Date(noticia.createdAt)
        .toISOString()
        .split("T")[0];
      const correspondePeriodo = !periodo || dataNoticia === periodo;
      const correspondeDescricao =
        !descricao ||
        normalizarTexto(noticia.title).includes(normalizarTexto(descricao));
      const correspondeCategoria = !categoria || noticia.category === categoria;

      return correspondePeriodo && correspondeDescricao && correspondeCategoria;
    });

    setFilteredNoticias(filtros);
    setPage(1);
  };

  const handleClear = () => {
    setPeriodo("");
    setDescricao("");
    setCategoria("");
    setFilteredNoticias(noticias);
  };

  const totalPaginas = Math.ceil(filteredNoticias.length / noticiasPorPagina);
  const noticiasPaginadas = filteredNoticias.slice(
    (page - 1) * noticiasPorPagina,
    page * noticiasPorPagina
  );

  
  const handleNoticiaClick = (noticiaId) => {
    navigate(`/noticia/${noticiaId}`); 
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Box sx={{ padding: 4, width: { xs: "95%", md: "70%" }, }}>
      <Grid container spacing={2} sx={{ marginBottom: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Data"
            type="date"
            fullWidth
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Título da notícia"
            fullWidth
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {categorias.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{ marginRight: 2 }}
          >
            Pesquisar
          </Button>
          <Button variant="outlined" onClick={handleClear}>
            Limpar
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {noticiasPaginadas.map((noticia) => (
          <Grid item xs={12} sm={6} md={4} key={noticia._id}>
            <Card
              onClick={() => handleNoticiaClick(noticia._id)}
              sx={{
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                height: "100%", 
              }}
            >
              <CardMedia
                component="img"
                height="140"
                image={`${noticia.CapaImage}`}
                alt={noticia.title}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography
                  sx={{
                    width: "40%",
                    textAlign: "center",
                    fontWeight: "500",
                    fontSize: "15px",
                    background: theme.palette.primary.main,
                    color: theme.palette.primary.text,
                    borderRadius: "10px",
                  }}
                >
                  {noticia.category}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {noticia.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {calcularDias(noticia.createdAt)} dias atrás
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
        <Pagination
          count={totalPaginas}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default Noticias;
